import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { FaBookOpen, FaChair, FaCheckCircle, FaDoorOpen, FaFilter, FaSearch, FaTimes } from 'react-icons/fa';
import axios from '../api/api1';
import FormSelect from '../components/FormSelect.jsx';
import UiSelect from '../components/UiSelect.jsx';
import { createSeat } from '../services/api.js';
import { useToast } from '../context/ToastContext.jsx';
import formatSeatNumber from '../utils/seatUtils.js';
import { useUrlFilters, intParam } from '../hooks/useUrlFilters.js';
import '../styles/pages/seats.css';

const seatIdOf = seat => seat?.seat_id ?? seat?.seatid ?? seat?.id;
const roomIdOf = room => room?.room_id ?? room?.roomid ?? room?.id;
const libraryIdOf = library => library?.id ?? library?.library_id;
const roomNameOf = room => room?.room_name || room?.roomname || room?.name || 'Room';
const libraryNameOf = library => library?.library_name || library?.name || '';
const studentIdOf = student => student?.student_id ?? student?.studentid ?? student?.id ?? student?._id;
const statusOf = seat => String(seat?.status || 'available').toLowerCase();
const categoryOf = seat => String(seat?.category || seat?.type || '').toLowerCase();

const isACSeat = seat => {
  const value = categoryOf(seat);
  return value === 'ac' || (value.includes('ac') && !value.includes('non'));
};

const isNonACSeat = seat => {
  const value = categoryOf(seat);
  return value === 'nonac' || value === 'non_ac' || value === 'non-ac' || value.includes('non');
};

const formatDate = value => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const normalizeSelectOption = option => (
  typeof option === 'string'
    ? { value: option, label: option }
    : { ...option, value: option?.value ?? option?.label ?? '', label: option?.label ?? option?.value ?? '' }
);

const normalizeCategoryOption = option => {
  const normalized = normalizeSelectOption(option);
  const key = String(normalized.value || normalized.label).toLowerCase().replace(/[\s_-]+/g, '');
  if (key === 'ac') return { ...normalized, value: 'ac', label: 'AC' };
  if (key.includes('non')) return { ...normalized, value: 'non_ac', label: 'Non-AC' };
  return normalized;
};

const dedupeOptions = options => {
  const seen = new Set();
  return options.filter(option => {
    const key = String(option.value ?? option.label ?? '').trim().toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const SeatsDirectory = forwardRef(function SeatsDirectory({ seats = [], rooms = [], libraries = [], students = [], seatTypes = {}, onRefresh, selectedLibrary = 'All Libraries' }, ref) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [libraryFilter, setLibraryFilter] = useState('all');
  const [roomFilter, setRoomFilter] = useState('all');
  const { showToast } = useToast();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [addOpen, setAddOpen] = useState(false);
  const [reserveSeat, setReserveSeat] = useState(null);
  const [confirmState, setConfirmState] = useState(null);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [addForm, setAddForm] = useState({ library_id: '', roomid: '', seatnumber: '', quantity: 1, category: 'ac', status: 'available' });
  const [studentSearch, setStudentSearch] = useState('');
  const [selectedStudent, setSelectedStudent] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);

  useUrlFilters([
    { key: 'search', value: search, setValue: setSearch, defaultValue: '' },
    { key: 'status', value: statusFilter, setValue: setStatusFilter, defaultValue: 'all' },
    { key: 'category', value: categoryFilter, setValue: setCategoryFilter, defaultValue: 'all' },
    { key: 'library', value: libraryFilter, setValue: setLibraryFilter, defaultValue: 'all' },
    { key: 'room', value: roomFilter, setValue: setRoomFilter, defaultValue: 'all' },
    { key: 'page', value: page, setValue: setPage, defaultValue: 1, parse: intParam(1, 100000) },
    { key: 'perPage', value: perPage, setValue: setPerPage, defaultValue: 10, parse: intParam(1, 100) },
  ]);
  const initialScope = useRef(selectedLibrary);
  const urlLibraryFilter = useRef(new URLSearchParams(window.location.search).has('library'));

  const seatStatusOptions = useMemo(() => {
    const options = seatTypes?.status?.length ? seatTypes.status : [
      { value: 'available', label: 'Available' },
      { value: 'occupied', label: 'Occupied' },
      { value: 'reserved', label: 'Reserved' },
    ];
    return dedupeOptions(options.map(option => {
      const normalized = normalizeSelectOption(option);
      const value = String(normalized.value).toLowerCase();
      return { ...normalized, label: value === 'occupied' || value === 'general' ? 'Occupied' : normalized.label };
    }));
  }, [seatTypes]);

  const seatCategoryOptions = useMemo(() => dedupeOptions(
    (seatTypes?.category?.length ? seatTypes.category : [
      { value: 'ac', label: 'AC' },
      { value: 'non_ac', label: 'Non-AC' },
    ]).map(normalizeCategoryOption)
  ), [seatTypes]);

  const statusFilterOptions = useMemo(() => dedupeOptions([
    { value: 'all', label: 'All Statuses' },
    ...seatStatusOptions,
  ]), [seatStatusOptions]);

  const categoryFilterOptions = useMemo(() => dedupeOptions([
    { value: 'all', label: 'All Categories' },
    ...seatCategoryOptions,
  ]), [seatCategoryOptions]);

  const libraryFilterOptions = useMemo(() => [
    { value: 'all', label: 'All Libraries' },
    ...libraries.map(library => ({ value: String(libraryIdOf(library)), label: libraryNameOf(library) })),
  ], [libraries]);

  useEffect(() => {
    if (!reserveSeat) return;
    setStudentSearch('');
    setSelectedStudent(String(reserveSeat.student_id || reserveSeat.studentid || ''));
  }, [reserveSeat]);

  useEffect(() => {
    if (urlLibraryFilter.current) {
      if (selectedLibrary === initialScope.current) return;
      urlLibraryFilter.current = false;
    }
    const allLibraries = !selectedLibrary || selectedLibrary === 'all' || selectedLibrary === 'All Libraries';
    if (allLibraries) {
      setLibraryFilter('all');
      return;
    }
    const match = libraries.find(library => (
      String(library.id ?? library.library_id) === String(selectedLibrary) ||
      String(library.library_name || library.name || '').toLowerCase() === String(selectedLibrary).toLowerCase()
    ));
    setLibraryFilter(match ? String(match.id ?? match.library_id) : String(selectedLibrary));
  }, [selectedLibrary, libraries]);

  const libraryRooms = useMemo(() => {
    if (libraryFilter === 'all') return rooms;
    return rooms.filter(room => {
      const roomLibraryId = room.library_id ?? room.libraryId ?? '';
      const roomLibraryName = room.library_name || room.libraryname || '';
      return String(roomLibraryId) === String(libraryFilter) ||
        String(roomLibraryName).toLowerCase() === String(libraryFilter).toLowerCase();
    });
  }, [rooms, libraryFilter]);

  const roomFilterOptions = useMemo(() => [
    { value: 'all', label: 'All Rooms' },
    ...libraryRooms.map(room => ({ value: String(roomIdOf(room)), label: roomNameOf(room) })),
  ], [libraryRooms]);

  useEffect(() => {
    if (roomFilter === 'all') return;
    if (!libraryRooms.some(room => String(roomIdOf(room)) === String(roomFilter))) setRoomFilter('all');
  }, [libraryRooms, roomFilter]);

  const filteredSeats = useMemo(() => {
    const query = search.trim().toLowerCase();
    return seats.filter(seat => {
      const status = statusOf(seat);
      const room = rooms.find(item => String(roomIdOf(item)) === String(roomIdOf(seat)));
      const roomLibraryId = seat.library_id ?? seat.libraryId ?? room?.library_id ?? room?.libraryId;
      const roomLibraryName = room?.library_name || room?.libraryname || '';
      const studentName = String(seat.student_name || seat.studentname || '').toLowerCase();
      const seatNumber = String(seat.seat_number || seat.seatnumber || seat.seatno || seat.seat_no || '').toLowerCase();
      if (query && !studentName.includes(query) && !seatNumber.includes(query)) return false;
      if (statusFilter !== 'all' && status !== statusFilter) return false;
      if (categoryFilter === 'ac' && !isACSeat(seat)) return false;
      if (categoryFilter === 'non_ac' && !isNonACSeat(seat)) return false;
      if (libraryFilter !== 'all' && String(roomLibraryId ?? '') !== String(libraryFilter) && String(roomLibraryName).toLowerCase() !== String(libraryFilter).toLowerCase()) return false;
      if (roomFilter !== 'all' && String(roomIdOf(seat)) !== String(roomFilter)) return false;
      return true;
    });
  }, [seats, rooms, search, statusFilter, categoryFilter, libraryFilter, roomFilter]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filteredSeats.length / perPage));
    if (page > totalPages) setPage(totalPages);
  }, [filteredSeats.length, perPage, page]);

  const totalPages = Math.max(1, Math.ceil(filteredSeats.length / perPage));
  const currentSeats = filteredSeats.slice((page - 1) * perPage, page * perPage);

  const stats = useMemo(() => ({
    total: filteredSeats.length,
    available: filteredSeats.filter(seat => statusOf(seat) === 'available').length,
    occupied: filteredSeats.filter(seat => statusOf(seat) === 'occupied').length,
    reserved: filteredSeats.filter(seat => statusOf(seat) === 'reserved').length,
    ac: filteredSeats.filter(isACSeat).length,
    nonAc: filteredSeats.filter(isNonACSeat).length,
  }), [filteredSeats]);

  const filtersActive = Boolean(search.trim()) || statusFilter !== 'all' || categoryFilter !== 'all' || libraryFilter !== 'all' || roomFilter !== 'all';

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('all');
    setCategoryFilter('all');
    setLibraryFilter('all');
    setRoomFilter('all');
    setPage(1);
  };

  const openAdd = useCallback(() => {
    setAddForm({ library_id: libraryFilter === 'all' ? '' : libraryFilter, roomid: '', seatnumber: '', quantity: 1, category: 'ac', status: 'available' });
    setErrors({});
    setAddOpen(true);
  }, [libraryFilter]);

  useImperativeHandle(ref, () => ({ openAdd }), [openAdd]);

  const refresh = message => {
    setConfirmState(null);
    setReserveSeat(null);
    setAddOpen(false);
    onRefresh?.();
    if (message) showToast(message, 'success');
  };

  const runSeatAction = async () => {
    if (!confirmState?.seat) return;
    setSaving(true);
    try {
      const id = seatIdOf(confirmState.seat);
      if (confirmState.action === 'occupy') {
        await axios.post(`/api/seats/${id}/occupy`, {});
      } else {
        await axios.post(`/api/seats/${id}/release`);
      }
      refresh(confirmState.action === 'occupy' ? 'Seat marked as occupied' : 'Seat released successfully');
    } catch (error) {
      showToast(error?.response?.data?.error || error?.message || 'Could not update seat', 'error');
    } finally {
      setSaving(false);
    }
  };

  const submitSeat = async event => {
    event.preventDefault();
    const nextErrors = {};
    if (!addForm.roomid) nextErrors.roomid = 'Select a room';
    if (!addForm.seatnumber.trim()) nextErrors.seatnumber = 'Enter a starting seat number';
    if (Number(addForm.quantity) < 1) nextErrors.quantity = 'Quantity must be at least 1';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setSaving(true);
    try {
      const result = await createSeat({
        seatnumber: addForm.seatnumber.trim(),
        quantity: Number(addForm.quantity),
        category: addForm.category,
        type: addForm.category === 'ac' ? 'AC' : 'Non-AC',
        roomid: Number(addForm.roomid),
        status: addForm.status,
      });
      const warnings = Array.isArray(result?.errors) && result.errors.length
        ? ` Some seats failed: ${result.errors.join(', ')}`
        : '';
      setAddForm({ library_id: '', roomid: '', seatnumber: '', quantity: 1, category: 'ac', status: 'available' });
      setErrors({});
      refresh(`${result?.message || 'Seats created successfully'}.${warnings}`);
    } catch (error) {
      showToast(error?.error || error?.message || 'Could not create seats', 'error');
    } finally {
      setSaving(false);
    }
  };

  const submitReservation = async event => {
    event.preventDefault();
    if (!selectedStudent) {
      showToast('Select a student to reserve this seat', 'error');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`/api/seats/${seatIdOf(reserveSeat)}/reserve`, { student_id: selectedStudent });
      refresh('Seat reserved successfully');
    } catch (error) {
      showToast(error?.response?.data?.error || error?.message || 'Could not reserve seat', 'error');
    } finally {
      setSaving(false);
    }
  };

  const reserveStudents = useMemo(() => {
    const query = studentSearch.trim().toLowerCase();
    return students
      .filter(student => !query || String(student.name || student.full_name || '').toLowerCase().includes(query) || String(student.phone || student.contact_no || '').includes(query))
      .slice(0, 200);
  }, [students, studentSearch]);

  const addRooms = libraryFilter === 'all'
    ? rooms
    : rooms.filter(room => {
      const roomLibraryId = room.library_id ?? room.libraryId ?? '';
      const roomLibraryName = room.library_name || room.libraryname || '';
      return String(roomLibraryId) === String(libraryFilter) ||
        String(roomLibraryName).toLowerCase() === String(libraryFilter).toLowerCase();
    });

  return (
    <div className="seats-directory">
      <div className="seat-summary-strip">
        <div><span>Total Seats</span><b>{stats.total}</b><small>AC {stats.ac}  Non-AC {stats.nonAc}</small></div>
        <div className="available"><span>Available</span><b>{stats.available}</b><small>Ready to book</small></div>
        <div className="reserved"><span>Reserved</span><b>{stats.reserved}</b><small>Personal seats</small></div>
        <div className="occupied"><span>Occupied</span><b>{stats.occupied}</b><small>{stats.total ? Math.round(stats.occupied / stats.total * 100) : 0}% occupancy</small></div>
      </div>

      <div className="filter-toggle-row">
        <button type="button" className="filter-toggle-btn" aria-expanded={filtersOpen} aria-controls="seat-filter-panel" onClick={() => setFiltersOpen(open => !open)}>
          <FaFilter aria-hidden="true" /><span>{filtersOpen ? 'Hide filters' : 'Show filters'}</span>
        </button>
      </div>

      {filtersOpen && <section id="seat-filter-panel" className="panel seat-filter-card" aria-label="Seat filters">
        <div className="seat-filter-head">
          <div className="seat-filter-title">
            <span className="seat-filter-icon" aria-hidden="true"><FaFilter /></span>
            <div>
              <b>Filters</b>
              <small>Refine seats by status, category, library, or room.</small>
            </div>
          </div>
          <span className="seat-filter-count"><b>{filteredSeats.length}</b> of <b>{seats.length}</b> seats</span>
        </div>
        <div className="seat-filter-controls">
          <label className="search-wrap seat-search">
            <FaSearch aria-hidden="true" />
            <input placeholder="Search student or seat number..." value={search} onChange={event => { setSearch(event.target.value); setPage(1); }} aria-label="Search seats" />
          </label>
          <FormSelect
            className="seat-filter-select"
            value={statusFilter}
            onChange={value => { setStatusFilter(value); setPage(1); }}
            options={statusFilterOptions}
            icon={<FaCheckCircle />}
            ariaLabel="Filter seats by status"
          />
          <FormSelect
            className="seat-filter-select"
            value={categoryFilter}
            onChange={value => { setCategoryFilter(value); setPage(1); }}
            options={categoryFilterOptions}
            icon={<FaChair />}
            ariaLabel="Filter seats by category"
          />
          <FormSelect
            className="seat-filter-select"
            value={libraryFilter}
            onChange={value => { setLibraryFilter(value); setRoomFilter('all'); setPage(1); }}
            options={libraryFilterOptions}
            icon={<FaBookOpen />}
            ariaLabel="Filter seats by library"
          />
          <FormSelect
            className="seat-filter-select"
            value={roomFilter}
            onChange={value => { setRoomFilter(value); setPage(1); }}
            options={roomFilterOptions}
            icon={<FaDoorOpen />}
            ariaLabel="Filter seats by room"
          />
          <button type="button" className="button ghost seat-filter-clear" onClick={clearFilters} disabled={!filtersActive}>
            <FaTimes aria-hidden="true" />
            <span>Clear filters</span>
          </button>
        </div>
      </section>}

      <div className="table-scroll seat-directory-table">
        <table>
          <thead><tr>{['Seat', 'Room', 'Category', 'Status', 'Student', 'Contact', 'Subscription Ends', 'Actions'].map(heading => <th key={heading}>{heading}</th>)}</tr></thead>
          <tbody>
            {currentSeats.map(seat => {
              const status = statusOf(seat);
              const room = rooms.find(item => String(roomIdOf(item)) === String(roomIdOf(seat)));
              const student = seat.student_name || seat.studentname;
              const contact = seat.student_email || seat.studentemail || seat.student_phone || seat.studentphone || '-';
              const expired = seat.subscription_end || seat.subscriptionend ? new Date(seat.subscription_end || seat.subscriptionend) < new Date() : false;
              return (
                <tr key={seatIdOf(seat)}>
                  <td><b className="seat-number-cell">{formatSeatNumber(seat.seat_number || seat.seatnumber || seat.seatno || seat.seat_no || seatIdOf(seat))}</b></td>
                  <td>{seat.room_name || seat.roomname || room?.room_name || room?.roomname || '-'}</td>
                  <td><span className={`seat-category ${isACSeat(seat) ? 'ac' : 'non-ac'}`}>{isACSeat(seat) ? 'AC' : 'Non-AC'}</span></td>
                  <td><span className={`seat-status ${status}`}>{status}</span></td>
                  <td>{status === 'available' ? '-' : student || (status === 'reserved' ? 'Reserved' : 'Occupied')}</td>
                  <td>{status === 'available' ? '-' : contact}</td>
                  <td>{status === 'available' ? '-' : <span className={expired ? 'expired' : ''}>{formatDate(seat.subscription_end || seat.subscriptionend)}{expired ? ' · Expired' : ''}</span>}</td>
                  <td>
                    <div className="seat-row-actions">
                      {status === 'available' && <>
                        <button type="button" onClick={() => setConfirmState({ action: 'occupy', seat })}>Occupy</button>
                        <button type="button" onClick={() => setReserveSeat(seat)}>Reserve</button>
                      </>}
                      {status !== 'available' && <button type="button" className="danger" onClick={() => setConfirmState({ action: 'release', seat })}>{status === 'reserved' ? 'Unreserve' : 'Release'}</button>}
                    </div>
                  </td>
                </tr>
              );
            })}
            {!currentSeats.length && <tr><td colSpan={8} className="empty-state">{seats.length ? 'No seats match these filters.' : 'No seats found. Add a seat to get started.'}</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="seat-directory-footer">
        <span>Showing <b>{currentSeats.length}</b> of <b>{filteredSeats.length}</b> seats</span>
        <div>
          <span>Rows</span>
          <UiSelect value={perPage} onChange={event => { setPerPage(Number(event.target.value)); setPage(1); }}>{[5, 10, 25, 50, 100].map(value => <option key={value} value={value}>{value}</option>)}</UiSelect>
          <button type="button" disabled={page <= 1} onClick={() => setPage(value => Math.max(1, value - 1))}>Previous</button>
          <span>Page <b>{page}</b> of <b>{totalPages}</b></span>
          <button type="button" disabled={page >= totalPages} onClick={() => setPage(value => Math.min(totalPages, value + 1))}>Next</button>
        </div>
      </div>

      {addOpen && (
        <div className="confirm-overlay" onClick={() => !saving && setAddOpen(false)}>
          <div className="confirm-box edit-box seat-modal seat-add-modal" onClick={event => event.stopPropagation()}>
            <div className="drawer-head"><h2>Add New Seat(s)</h2><button type="button" className="close-x" onClick={() => setAddOpen(false)} aria-label="Close">Ã—</button></div>
            <form className="modal-form seat-add-form" onSubmit={submitSeat}>
              <div className="seat-add-grid">
                <div className="field"><label>Library</label><UiSelect value={addForm.library_id} onChange={event => setAddForm({ ...addForm, library_id: event.target.value, roomid: '' })}><option value="">All Libraries</option>{libraries.map(library => <option key={library.id ?? library.library_id} value={library.id ?? library.library_id}>{library.library_name || library.name}</option>)}</UiSelect></div>
                <div className="field"><label>Room <em>*</em></label><UiSelect value={addForm.roomid} onChange={event => setAddForm({ ...addForm, roomid: event.target.value })} required><option value="">Select Room</option>{addRooms.map(room => <option key={roomIdOf(room)} value={roomIdOf(room)}>{room.room_name || room.roomname || room.name}</option>)}</UiSelect>{errors.roomid && <small className="form-error">{errors.roomid}</small>}</div>
                <div className="field"><label>Starting Seat Number <em>*</em></label><input value={addForm.seatnumber} onChange={event => setAddForm({ ...addForm, seatnumber: event.target.value })} placeholder="e.g. A1, S101" required />{errors.seatnumber && <small className="form-error">{errors.seatnumber}</small>}</div>
                <div className="field"><label>Quantity</label><input type="number" min="1" max="50" value={addForm.quantity} onChange={event => setAddForm({ ...addForm, quantity: event.target.value })} />{errors.quantity && <small className="form-error">{errors.quantity}</small>}</div>
                <div className="field"><label>Category</label><UiSelect value={addForm.category} onChange={event => setAddForm({ ...addForm, category: event.target.value })}>{seatCategoryOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}</UiSelect></div>
                <div className="field"><label>Initial Status</label><UiSelect value={addForm.status} onChange={event => setAddForm({ ...addForm, status: event.target.value })}>{seatStatusOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}</UiSelect></div>
              </div>
              <small className="muted-note seat-add-note">Creates sequential seats like A1, A2, A3.</small>
              <div className="confirm-actions seat-add-actions"><button type="button" className="button ghost" onClick={() => setAddOpen(false)} disabled={saving}>Cancel</button><button type="submit" className="button primary" disabled={saving}>{saving ? 'Creating...' : `Create ${Number(addForm.quantity) > 1 ? `${addForm.quantity} Seats` : 'Seat'}`}</button></div>
            </form>
          </div>
        </div>
      )}

      {reserveSeat && (
        <div className="confirm-overlay" onClick={() => !saving && setReserveSeat(null)}>
          <div className="confirm-box seat-modal" onClick={event => event.stopPropagation()}>
            <div className="drawer-head"><h2>Reserve Seat {formatSeatNumber(reserveSeat.seat_number || reserveSeat.seatnumber)}</h2><button type="button" className="close-x" onClick={() => setReserveSeat(null)}>Ãƒ</button></div>
            <form className="modal-form" onSubmit={submitReservation}>
              <div className="field"><label>Search Student</label><input value={studentSearch} onChange={event => setStudentSearch(event.target.value)} placeholder="Search by name or phone..." /></div>
              <div className="field"><label>Student *</label><UiSelect value={selectedStudent} onChange={event => setSelectedStudent(event.target.value)} required><option value="">-- Select Student --</option>{reserveStudents.map(student => <option key={studentIdOf(student)} value={studentIdOf(student)}>{student.name || student.full_name || 'Unknown'} ({student.phone || student.contact_no || 'No phone'})</option>)}</UiSelect></div>
              <div className="confirm-actions"><button type="button" className="button ghost" onClick={() => setReserveSeat(null)} disabled={saving}>Cancel</button><button type="submit" className="button primary" disabled={saving}>{saving ? 'Reserving...' : 'Confirm Reservation'}</button></div>
            </form>
          </div>
        </div>
      )}

      {confirmState && (
        <div className="confirm-overlay" onClick={() => !saving && setConfirmState(null)}>
          <div className="confirm-box seat-confirm" onClick={event => event.stopPropagation()}>
            <div className="drawer-head"><h2>{confirmState.action === 'occupy' ? 'Mark Seat Occupied' : confirmState.action === 'release' && statusOf(confirmState.seat) === 'reserved' ? 'Unreserve Seat' : 'Release Seat'}</h2></div>
            <p>Seat <b>{formatSeatNumber(confirmState.seat.seat_number || confirmState.seat.seatnumber)}</b> will be {confirmState.action === 'occupy' ? 'marked as occupied' : 'released'}. Continue?</p>
            <div className="confirm-actions"><button type="button" className="button ghost" onClick={() => setConfirmState(null)} disabled={saving}>Cancel</button><button type="button" className="button primary" onClick={runSeatAction} disabled={saving}>{saving ? 'Updating...' : 'Confirm'}</button></div>
          </div>
        </div>
      )}
    </div>
  );
});

SeatsDirectory.displayName = 'SeatsDirectory';

